import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  {
    before:
      "https://customer-assets.emergentagent.com/job_clean-toronto/artifacts/jidb46oi_IMG_6658.jpeg",
    after:
      "https://customer-assets.emergentagent.com/job_clean-toronto/artifacts/33kwuczu_IMG_6659.jpeg",
    label: "Real Pristyn job · Laundry tile deep clean",
  },
  {
    before:
      "https://customer-assets.emergentagent.com/job_clean-toronto/artifacts/wo0exz7g_IMG_6653.jpeg",
    after:
      "https://customer-assets.emergentagent.com/job_clean-toronto/artifacts/yk5qy7rn_IMG_6654.jpeg",
    label: "Real Pristyn job · Bathroom vanity restoration",
  },
];

export default function BeforeAfter() {
  const [idx, setIdx] = useState(0);
  const [reveal, setReveal] = useState(50);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % SLIDES.length);
      setReveal(50);
    }, 6500);
    return () => clearInterval(intervalRef.current);
  }, []);

  const slide = SLIDES[idx];

  const go = (dir) => {
    setIdx((i) => (i + dir + SLIDES.length) % SLIDES.length);
    setReveal(50);
  };

  return (
    <section
      id="transformations"
      data-testid="before-after-section"
      className="relative bg-black py-24 text-white md:py-32"
    >
      <div className="pc-container">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <div className="eyebrow text-[var(--pc-green)]">Real Pristyn Jobs</div>
            <h2 className="font-display mt-3 max-w-2xl text-4xl font-black tracking-tighter sm:text-5xl">
              Drag the slider. See the difference.
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="ba-prev"
              onClick={() => go(-1)}
              className="grid h-12 w-12 place-items-center rounded-full border border-white/20 transition hover:border-[var(--pc-green)] hover:bg-[var(--pc-green)] hover:text-black"
              aria-label="Previous"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              data-testid="ba-next"
              onClick={() => go(1)}
              className="grid h-12 w-12 place-items-center rounded-full border border-white/20 transition hover:border-[var(--pc-green)] hover:bg-[var(--pc-green)] hover:text-black"
              aria-label="Next"
            >
              <ChevronRight size={22} />
            </button>
          </div>
        </div>

        <div
          data-testid="ba-stage"
          className="relative mt-10 aspect-[16/10] w-full select-none overflow-hidden rounded-3xl border border-white/10 bg-black"
        >
          <img
            src={slide.after}
            alt="After"
            className="absolute inset-0 h-full w-full object-cover"
            draggable={false}
          />
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ width: `${reveal}%` }}
          >
            <img
              src={slide.before}
              alt="Before"
              className="h-full w-full object-cover"
              style={{ width: `${(100 / reveal) * 100}%`, maxWidth: "none" }}
              draggable={false}
            />
          </div>

          <div
            className="pointer-events-none absolute inset-y-0 z-10 w-[2px] bg-[var(--pc-green)]"
            style={{ left: `${reveal}%` }}
          />
          <div
            className="pointer-events-none absolute z-10 grid h-12 w-12 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-[var(--pc-green)] text-black shadow-[0_8px_24px_rgba(0,230,118,0.5)]"
            style={{ left: `${reveal}%`, top: "50%" }}
          >
            <ChevronLeft size={14} />
            <ChevronRight size={14} className="-ml-1.5" />
          </div>

          <input
            data-testid="ba-slider"
            aria-label="Reveal slider"
            type="range"
            min={5}
            max={95}
            value={reveal}
            onChange={(e) => setReveal(Number(e.target.value))}
            className="absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
          />

          <div className="absolute left-5 top-5 z-10 rounded-full bg-black/60 px-3 py-1.5 text-xs uppercase tracking-[0.2em]">
            Before
          </div>
          <div className="absolute right-5 top-5 z-10 rounded-full bg-[var(--pc-green)] px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-black">
            After
          </div>
          <div className="absolute bottom-5 left-5 z-10 max-w-[70%] rounded-full bg-black/60 px-4 py-2 text-sm">
            {slide.label}
          </div>
        </div>

        <div className="mt-6 flex justify-center gap-2">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`ba-dot-${i}`}
              onClick={() => {
                setIdx(i);
                setReveal(50);
              }}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-10 bg-[var(--pc-green)]" : "w-4 bg-white/30"
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
